import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { z } from 'zod'
import { ChevronDownIcon, SearchIcon } from 'lucide-react'
import {
	Checkbox,
	DatePicker,
	Form, FormControl, FormMessage, FormField, FormItem, FormLabel,
	Input,
	PhoneInput,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { useGetContactKycQuery, useListContactsQuery } from '@/api'
import { formatPhone, phoneOrEmpty, isMaskSafePhone } from '@/lib/phone'
import type { Contact } from '@/types'
import { EditableSectionHeader } from './editable-section-header'
import { kycToTenantType } from './index'
import { TENANT_TYPES, type TenantType, type TenantValues } from './types'

/**
 * Required where the contract cannot identify the party without it.
 *
 * Everything used to be a bare `z.string()`, so the wizard walked through with
 * an empty tenant and printed «паспорт серии  № , выдан ,» into a signed
 * document — text that reads as filled in while naming nobody.
 *
 * Passport fields are demanded of individuals only: an entrepreneur or a
 * company is identified by its INN and name, and has no passport to give.
 *
 * All of that applies to the documents that PRINT those fields — the lease
 * contracts. An act, appendix or registration application names the tenant but
 * carries no requisites block, so demanding a bank account there only blocks a
 * document that never mentions one.
 */
const requiredText = (key: string) => z.string().trim().min(1, key)

const V = 'deals.documents.wizard.tenant.validation'

const makeTenantSchema = (printsRequisites: boolean) => z.object({
	contragentId:        z.string(),
	contragentName:      requiredText(`${V}.nameRequired`),
	tenantType:          z.enum(TENANT_TYPES as [TenantType, ...TenantType[]]),
	registrationAddress: requiredText(`${V}.addressRequired`),
	// Printed into the contract as the tenant's contact detail. Was a bare
	// z.string() with no message slot — the one field left in the app that would
	// accept "5egergerg" and carry it into a signed document.
	phone:               phoneOrEmpty('deals.documents.wizard.tenant.validation.phoneInvalid'),
	passportSeries:      z.string(),
	passportNumber:      z.string(),
	passportIssuedBy:    z.string(),
	inn:                 z.string(),
	ogrn:                z.string(),
	kpp:                 z.string(),
	byProxy:             z.boolean(),
	signatoryId:         z.string(),
	baseDocument:        z.string(),
	baseDocumentNumber:  z.string(),
	validFromIso:        z.string(),
	validToIso:          z.string(),
	// Printed verbatim into the requisites block. Left optional they produced
	// «Банк: , р/с: , к/с: , БИК:» in a signed contract — punctuation around
	// nothing, which reads as filled in while stating no account at all.
	bank:                printsRequisites ? requiredText(`${V}.bankRequired`)        : z.string(),
	bankAccount:         printsRequisites ? requiredText(`${V}.bankAccountRequired`) : z.string(),
	corrAccount:         printsRequisites ? requiredText(`${V}.corrAccountRequired`) : z.string(),
	bic:                 printsRequisites ? requiredText(`${V}.bicRequired`)         : z.string(),
}).superRefine((values, ctx) => {
	if (!printsRequisites) return

	// A company or a sole trader is identified by its INN; without it the
	// contract names a party that cannot be told from any namesake.
	if (values.tenantType !== 'individual') {
		if (!values.inn.trim()) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['inn'],
				message: `${V}.innRequired`,
			})
		}
		return
	}

	const passportFields = [
		['passportSeries', `${V}.passportSeriesRequired`],
		['passportNumber', `${V}.passportNumberRequired`],
		['passportIssuedBy', `${V}.passportIssuedByRequired`],
	] as const

	for (const [field, message] of passportFields) {
		if (!values[field].trim()) {
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message })
		}
	}
})

type TenantFormSchema = z.infer<ReturnType<typeof makeTenantSchema>>

interface ContragentComboboxProps {
	value:         string
	disabled:      boolean
	placeholder:   string
	onPickContact: (contact: Contact) => void
	onCreateNew:   (name: string) => void
}

const isoToDate = (iso?: string): Date | undefined => (iso ? new Date(iso) : undefined)
const dateToIso = (d?: Date): string                => (d ? d.toISOString().slice(0, 10) : '')

function ContragentCombobox({ value, disabled, placeholder, onPickContact, onCreateNew }: ContragentComboboxProps) {
	const { t } = useTranslation()
	const [open, setOpen]   = useState(false)
	const [query, setQuery] = useState('')
	const ref = useRef<HTMLDivElement>(null)

	const { data } = useListContactsQuery({ type: 'TENANT', search: query, limit: 20 })
	const items = data?.items ?? []
	const exactMatch = items.find((c) => c.name.toLowerCase() === query.trim().toLowerCase())

	useEffect(() => {
		if (!open) return
		function handleDocClick(e: MouseEvent) {
			if (!ref.current) return
			if (e.target instanceof Node && !ref.current.contains(e.target)) {
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', handleDocClick)
		return () => document.removeEventListener('mousedown', handleDocClick)
	}, [open])

	return (
		<div className="relative" ref={ref}>
			<button
				type="button"
				disabled={disabled}
				onClick={() => setOpen((v) => !v)}
				className={`flex h-10 w-full items-center justify-between rounded-(--radius-md) border border-(--color-border) bg-(--color-background) px-3 text-sm text-left transition-colors ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-(--color-muted)/40'}`}
			>
				<span className={value ? 'text-(--color-foreground) truncate' : 'text-(--color-muted-foreground)'}>
					{value || placeholder}
				</span>
				<ChevronDownIcon className="size-4 shrink-0 text-(--color-muted-foreground)" />
			</button>

			{open && (
				<div
					className="absolute left-0 right-0 top-full z-50 mt-1 flex flex-col overflow-hidden rounded-(--radius-md) border border-(--color-border) bg-(--color-popover) shadow-(--shadow-md)"
					style={{ maxHeight: 320 }}
				>
					<div className="flex shrink-0 items-center gap-2 border-b border-(--color-border) px-3 py-2.5">
						<SearchIcon className="size-4 shrink-0 text-(--color-muted-foreground)" />
						<input
							type="text"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={t('deals.documents.wizard.tenant.searchPlaceholder')}
							className="flex-1 bg-transparent text-sm leading-5 text-(--color-foreground) placeholder:text-(--color-muted-foreground) focus:outline-none"
							autoFocus
						/>
					</div>

					<div className="flex-1 overflow-y-auto py-1">
						{items.length === 0 && !query && (
							<div className="px-3 py-3 text-sm text-(--color-muted-foreground)">
								{t('deals.documents.wizard.tenant.empty')}
							</div>
						)}
						{items.map((c) => (
							<button
								key={c.id}
								type="button"
								onClick={() => {
									onPickContact(c)
									setOpen(false)
									setQuery('')
								}}
								className="flex w-full flex-col items-start px-3 py-2 text-left text-sm transition-colors hover:bg-(--color-accent)"
							>
								<span className="text-(--color-foreground)">{c.name}</span>
								{c.phone && <span className="text-(--color-muted-foreground) text-xs">{formatPhone(c.phone)}</span>}
							</button>
						))}
					</div>

					{query.trim() && !exactMatch && (
						<button
							type="button"
							onClick={() => {
								onCreateNew(query.trim())
								setOpen(false)
								setQuery('')
							}}
							className="flex w-full shrink-0 items-center gap-2 border-t border-(--color-border) px-3 py-2 text-sm text-left transition-colors hover:bg-(--color-accent)"
						>
							<span className="text-(--color-foreground)">
								{t('deals.documents.wizard.tenant.useAsNew', { name: query.trim() })}
							</span>
						</button>
					)}
				</div>
			)}
		</div>
	)
}

interface StepTenantProps {
	initial: TenantValues
	formId:  string
	/** Drives which fields are hard-required — see makeTenantSchema. */
	documentType: string
	onValid: (values: TenantValues) => void
}

export function StepTenant({ initial, formId, documentType, onValid }: StepTenantProps) {
	const { t } = useTranslation()

	// Tenant section read-only by default; pencil enables overrides. Bank
	// requisites have their own pencil since they're often edited independently
	// (different bank than the registered Contact).
	const [editable,     setEditable]     = useState(false)
	const [bankEditable, setBankEditable] = useState(false)

	const form = useForm<TenantFormSchema>({
		resolver: zodResolverI18n(makeTenantSchema(documentType === 'LEASE_CONTRACT')),
		defaultValues: {
			contragentId:        initial.contragentId,
			contragentName:      initial.contragentName,
			tenantType:          initial.tenantType,
			registrationAddress: initial.registrationAddress,
			phone:               initial.phone,
			passportSeries:      initial.passportSeries,
			passportNumber:      initial.passportNumber,
			passportIssuedBy:    initial.passportIssuedBy,
			inn:                 initial.inn,
			ogrn:                initial.ogrn,
			kpp:                 initial.kpp,
			byProxy:             initial.byProxy ?? false,
			signatoryId:         initial.signatoryId,
			baseDocument:        initial.baseDocument,
			baseDocumentNumber:  initial.baseDocumentNumber,
			validFromIso:        initial.validFromIso,
			validToIso:          initial.validToIso,
			bank:                initial.bank,
			bankAccount:         initial.bankAccount,
			corrAccount:         initial.corrAccount,
			bic:                 initial.bic,
		},
		mode: 'onChange',
	})

	const contragentId = form.watch('contragentId')
	const byProxy      = form.watch('byProxy')
	const tenantType   = form.watch('tenantType')
	const isIndividual = tenantType === 'individual'

	// Pull KYC details for the picked contact so we can autofill the rest of
	// the form. RTK Query caches per-id; skipped when no contact is picked.
	const { data: kyc } = useGetContactKycQuery(contragentId, { skip: !contragentId })

	// The last value the phone field was SEEDED with from stored data — the
	// contract's own `initial`, a picked contact, or that contact's KYC record.
	// Decides masked vs plain input (see isMaskSafePhone); a ref rather than
	// state because it must not follow what the user types, and every write to
	// it is paired with a form.setValue that re-renders the phone controller
	// which reads it.
	const seededPhoneRef = useRef(initial.phone)

	// Autofill happens ONCE per contragent change — we don't want to clobber the
	// user's subsequent edits to fields after the initial pick.
	const lastFilledIdRef = useRef<string | null>(null)
	useEffect(() => {
		if (!kyc) return
		if (lastFilledIdRef.current === kyc.contactId) return
		lastFilledIdRef.current = kyc.contactId
		const set = (k: keyof TenantFormSchema, v: string | undefined) => {
			if (v) form.setValue(k, v as never, { shouldValidate: true })
		}
		set('contragentName',      kyc.fullName)
		set('registrationAddress', kyc.registrationAddr)
		if (kyc.phone) seededPhoneRef.current = kyc.phone
		set('phone',               kyc.phone)
		set('passportSeries',      kyc.passportSeries)
		set('passportNumber',      kyc.passportNumber)
		set('passportIssuedBy',    kyc.passportIssuedBy)
		set('inn',                 kyc.inn)
		set('ogrn',                kyc.ogrn)
		set('kpp',                 kyc.kpp)
		set('signatoryId',         kyc.signatory)
		set('bank',                kyc.bank)
		set('bankAccount',         kyc.bankAccount)
		set('corrAccount',         kyc.corrAccount)
		set('bic',                 kyc.bik)
		if (kyc.tenantType) {
			form.setValue('tenantType', kycToTenantType(kyc.tenantType), { shouldValidate: true })
		}
	}, [kyc, form])

	function pickContact(contact: Contact) {
		form.setValue('contragentId',   contact.id,   { shouldValidate: true })
		form.setValue('contragentName', contact.name, { shouldValidate: true })
		if (contact.phone) {
			seededPhoneRef.current = contact.phone
			form.setValue('phone', contact.phone, { shouldValidate: true })
		}
	}

	function createNew(name: string) {
		form.setValue('contragentId',   '',   { shouldValidate: true })
		form.setValue('contragentName', name, { shouldValidate: true })
		lastFilledIdRef.current = null
	}

	function handleSubmit(values: TenantFormSchema) {
		onValid(values)
	}

	return (
		<Form {...form}>
			<form
				id={formId}
				onSubmit={form.handleSubmit(handleSubmit)}
				className="flex flex-col gap-4"
			>
				<EditableSectionHeader
					title={t('deals.documents.wizard.tenant.title')}
					editable={editable}
					onToggle={() => setEditable((v) => !v)}
					editLabel={t('deals.documents.wizard.editSection')}
				/>

				<div className="flex gap-2">
					<FormField
						control={form.control}
						name="contragentName"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('deals.documents.wizard.tenant.contragent')}</FormLabel>
								<ContragentCombobox
									value={field.value}
									disabled={!editable}
									placeholder={t('deals.documents.wizard.tenant.contragentPlaceholder')}
									onPickContact={pickContact}
									onCreateNew={createNew}
								/>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="tenantType"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('deals.documents.wizard.tenant.tenantType')}</FormLabel>
								<Select value={field.value} onValueChange={field.onChange} disabled={!editable}>
									<FormControl>
										<SelectTrigger size="md" className="w-full">
											<SelectValue />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										<SelectItem value="individual">{t('deals.documents.wizard.tenant.tenantTypeIndividual')}</SelectItem>
										<SelectItem value="entrepreneur">{t('deals.documents.wizard.tenant.tenantTypeEntrepreneur')}</SelectItem>
										<SelectItem value="company">{t('deals.documents.wizard.tenant.tenantTypeCompany')}</SelectItem>
									</SelectContent>
								</Select>
							</FormItem>
						)}
					/>
				</div>

				<div className="flex gap-2">
					<FormField
						control={form.control}
						name="registrationAddress"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('deals.documents.wizard.tenant.registrationAddress')}</FormLabel>
								<FormControl>
									<Input {...field} disabled={!editable} placeholder={t('deals.documents.wizard.tenant.registrationAddressPlaceholder')} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="phone"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('deals.documents.wizard.tenant.phone')}</FormLabel>
								<FormControl>
									{isMaskSafePhone(seededPhoneRef.current) ? (
										<PhoneInput value={field.value ?? ''} onChange={field.onChange} disabled={!editable} />
									) : (
										<Input {...field} disabled={!editable} placeholder={t('deals.documents.wizard.tenant.phonePlaceholder')} />
									)}
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				{/* A passport identifies an individual; a company or a sole trader is
				    identified by its registry numbers. Showing both would ask every
				    tenant for documents half of them do not have. */}
				{isIndividual && (
				<>
				{/* Раньше здесь был один FormItem на два поля: оба FormControl брали
				    его идентификатор, метка указывала на то из них, что нарисовано
				    первым, а сообщения об ошибке было некуда положить — отсюда и
				    самодельный абзац под строкой. Теперь у каждого поля свой. */}
				<div className="flex flex-col gap-2">
					<p className="text-sm leading-none font-[500] text-(--color-foreground)">
						{t('deals.documents.wizard.tenant.passport')}
					</p>
					<div className="flex gap-2">
						<FormField
							control={form.control}
							name="passportSeries"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormControl>
										<Input
											{...field}
											disabled={!editable}
											prefix={t('deals.documents.wizard.tenant.passportSeries')}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="passportNumber"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormControl>
										<Input
											{...field}
											disabled={!editable}
											prefix={t('deals.documents.wizard.tenant.passportNumber')}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>

				<FormField
					control={form.control}
					name="passportIssuedBy"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('deals.documents.wizard.tenant.passportIssuedBy')}</FormLabel>
							<FormControl>
								<Input {...field} disabled={!editable} placeholder={t('deals.documents.wizard.tenant.passportIssuedByPlaceholder')} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				</>
				)}

				{!isIndividual && (
					<div className="flex gap-2">
						<FormField
							control={form.control}
							name="inn"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.tenant.inn')}</FormLabel>
									<FormControl>
										<Input {...field} disabled={!editable} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="ogrn"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.tenant.ogrn')}</FormLabel>
									<FormControl>
										<Input {...field} disabled={!editable} />
									</FormControl>
								</FormItem>
							)}
						/>
						{/* КПП is assigned to organisations only — a sole trader has none. */}
						{tenantType === 'company' && (
							<FormField
								control={form.control}
								name="kpp"
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormLabel>{t('deals.documents.wizard.tenant.kpp')}</FormLabel>
										<FormControl>
											<Input {...field} disabled={!editable} />
										</FormControl>
									</FormItem>
								)}
							/>
						)}
					</div>
				)}

				<FormField
					control={form.control}
					name="byProxy"
					render={({ field }) => (
						<FormItem className="flex flex-row items-center gap-2 space-y-0 pt-1">
							<FormControl>
								<Checkbox
									checked={field.value}
									onCheckedChange={(v) => field.onChange(v === true)}
									disabled={!editable}
								/>
							</FormControl>
							<FormLabel className="font-[400] cursor-pointer">
								{t('deals.documents.wizard.tenant.byProxy')}
							</FormLabel>
						</FormItem>
					)}
				/>

				{byProxy && (
					<>
						<FormField
							control={form.control}
							name="signatoryId"
							render={({ field }) => (
								<FormItem className="w-1/2 pr-1">
									<FormLabel>{t('deals.documents.wizard.tenant.signatory')}</FormLabel>
									<FormControl>
										<Input {...field} disabled={!editable} />
									</FormControl>
								</FormItem>
							)}
						/>

						<div className="flex gap-2">
							<FormField
								control={form.control}
								name="baseDocument"
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormLabel>{t('deals.documents.wizard.tenant.baseDocument')}</FormLabel>
										<FormControl>
											<Input {...field} disabled={!editable} placeholder={t('deals.documents.wizard.tenant.baseDocumentOption')} />
										</FormControl>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="baseDocumentNumber"
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormLabel>{t('deals.documents.wizard.tenant.baseDocumentNumber')}</FormLabel>
										<FormControl>
											<Input {...field} disabled={!editable} placeholder={t('deals.documents.wizard.tenant.baseDocumentNumberPlaceholder')} />
										</FormControl>
									</FormItem>
								)}
							/>
						</div>

						<FormItem>
							<FormLabel>{t('deals.documents.wizard.tenant.validRange')}</FormLabel>
							<div className="flex items-center">
								<FormField
									control={form.control}
									name="validFromIso"
									render={({ field }) => (
										<FormControl>
											<DatePicker
												value={isoToDate(field.value as string | undefined)}
												onChange={(d) => field.onChange(dateToIso(d))}
												disabled={!editable}
												className="flex-1"
											/>
										</FormControl>
									)}
								/>
								<span className="w-8 h-[42px] flex items-center justify-center text-sm leading-5 text-(--color-muted-foreground)">—</span>
								<FormField
									control={form.control}
									name="validToIso"
									render={({ field }) => (
										<FormControl>
											<DatePicker
												value={isoToDate(field.value as string | undefined)}
												onChange={(d) => field.onChange(dateToIso(d))}
												disabled={!editable}
												className="flex-1"
											/>
										</FormControl>
									)}
								/>
							</div>
						</FormItem>
					</>
				)}

				<section className="mt-2 flex flex-col gap-4">
					<EditableSectionHeader
						title={t('deals.documents.wizard.tenant.bankDetails')}
						editable={bankEditable}
						onToggle={() => setBankEditable((v) => !v)}
						editLabel={t('deals.documents.wizard.editSection')}
					/>

					<div className="flex gap-2">
						<FormField
							control={form.control}
							name="bank"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.tenant.bank')}</FormLabel>
									<FormControl>
										<Input {...field} disabled={!bankEditable} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="bankAccount"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.tenant.bankAccount')}</FormLabel>
									<FormControl>
										<Input {...field} disabled={!bankEditable} placeholder="1234" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<div className="flex gap-2">
						<FormField
							control={form.control}
							name="corrAccount"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.tenant.corrAccount')}</FormLabel>
									<FormControl>
										<Input {...field} disabled={!bankEditable} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="bic"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.tenant.bic')}</FormLabel>
									<FormControl>
										<Input {...field} disabled={!bankEditable} placeholder="1234" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</section>
			</form>
		</Form>
	)
}
